import { useTranslation } from 'react-i18next';
import { formatDaySeparator } from '@/lib/format';
import { CheckIcon } from '@/components/icons';

export function DaySeparator(props: { date: Date; unread?: boolean }) {
  const { t } = useTranslation();
  // Discord-style: a hairline with the date centred. When this boundary is
  // also the unread cutoff, the rule turns danger-red and a "НОВОЕ" pill
  // rides the right edge — one combined divider instead of two stacked ones.
  // The date itself is quiet: a small centred label between two hairlines,
  // no glass pill. It marks a boundary; it isn't content.
  const lineClass = props.unread ? 'h-px flex-1 bg-danger/50' : 'h-px flex-1 bg-white/[0.06]';
  return (
    <div className="my-5 flex items-center gap-3 px-2">
      <div className={lineClass} />
      <span className="text-[11px] font-medium tabular-nums tracking-[0.01em] text-ink-muted">
        {formatDaySeparator(props.date)}
      </span>
      <div className={lineClass} />
      {props.unread && (
        <span className="font-semibold shrink-0 rounded-full bg-danger px-2 py-0.5 text-[10px] uppercase tracking-wider text-white">
          {t('ui2.separator.new')}
        </span>
      )}
    </div>
  );
}

export function UnreadSeparator() {
  const { t } = useTranslation();
  // Full-width danger rule + "НОВОЕ" pill on the right (Discord parity).
  // Reads clearly on light and dark — the danger token + white pill text
  // hold contrast on both surfaces.
  return (
    <div className="my-3 flex items-center gap-2">
      <div className="h-px flex-1 bg-danger/50" />
      <span className="font-semibold shrink-0 rounded-full bg-danger px-2 py-0.5 text-[10px] uppercase tracking-wider text-white">
        {t('ui2.separator.new')}
      </span>
    </div>
  );
}

/** ✓ sent / ✓✓ read indicator for own 1:1 DM messages. "Read" = the peer's
 *  read pointer was last advanced at or after this message was created. */
export function ReadReceipt(props: { readAt: string | null; createdAt: string }) {
  const { t } = useTranslation();
  const read =
    props.readAt != null &&
    new Date(props.readAt).getTime() >= new Date(props.createdAt).getTime();
  return (
    <span
      className="relative inline-flex w-[15px] items-center"
      title={read ? t('ui2.separator.read') : t('ui2.separator.sent')}
      aria-label={read ? t('ui2.separator.read') : t('ui2.separator.sent')}
    >
      <CheckIcon className={'h-3 w-3 ' + (read ? 'text-accent' : 'text-ink-muted')} />
      {read && <CheckIcon className="absolute left-[4px] h-3 w-3 text-accent" />}
    </span>
  );
}
